import React, { useEffect } from 'react'
import { Box, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, styled, tableCellClasses } from '@mui/material'
import Pagination from '../../../components/TablePagination'
import { visuallyHidden } from '@mui/utils'
import { type ConnectedProps, connect } from 'react-redux'
import { type RootState } from '../../../store'
import { fetchConfigurationsForStatus } from '../redux/actionCreator'
import { type ConfigurationsForStatusState } from '../redux/configurationListForStatusSlice'
import { type ConfigurationObj, type GroupParaObj } from '../services/configurationServiceForStatus'
import AuthError from '../../../components/ErrorComponents/AuthError'
import successBadge from '../../../assets/successBadge.svg'
import progressBadge from '../../../assets/progressBadge.svg'
import failedBadge from '../../../assets/failedBadge.svg'
import notApplicableBadge from '../../../assets/notApplicableBadge.svg'
import { type TemplateDetailsState, changeTemplate } from '../redux/selectedTemplateDetailsSlice'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import LanguageStrings from '../../../i18n/locales'
import { useAuth } from 'react-oidc-context'
import { getGroupModel } from '../../asset-configuration/components/getGroupModel'

const ConfigurationListStrings = LanguageStrings().ConfigurationUploadStrings.components.ConfigurationList

const style = {
  width: '99%',
  bgcolor: '#313030',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0
}

function EnhancedTableHead (props: any): any {
  const { order, orderBy, onRequestSort } = props
  const createSortHandler = (newOrderBy: keyof ConfigurationObj) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, newOrderBy)
  }

  return (
    <TableHead data-testid="table-header">
      <TableRow>
        {headCells.map((headCell: HeadCell) => (
          <StyledTableCell
            key={headCell.id}
            align={'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.id === 'groups'
              ? headCell.label
              : (
                <TableSortLabel
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                  aria-label={headCell.label}
                  data-testid="test-sort"
                  sx={{
                    color: '#FFFFFF', // Set text color to white
                    textDecoration: 'none' // Remove underline
                  }}
                >
                  {headCell.label}
                  {orderBy === headCell.id
                    ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                      )
                    : null}
                </TableSortLabel>
                )
            }
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
  numeric: boolean
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '700',
    fontSize: 12,
    border: '2px solid #202020',
    padding: '5px 16px',
    fontFamily: 'Honeywell Sans Web'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '17px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web'
  }
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& th': {
    border: '1px solid #202020'
  }
}))

const headCells: readonly HeadCell[] = [
  {
    id: 'template_name',
    numeric: false,
    disablePadding: false,
    label: ConfigurationListStrings.CONFIGURATION_NAME
  },
  {
    id: 'asset_type_name',
    numeric: false,
    disablePadding: false,
    label: ConfigurationListStrings.CONFIGURATION_TYPE
  },
  {
    id: 'assets_associated',
    numeric: true,
    disablePadding: false,
    label: ConfigurationListStrings.TOTAL_ASSET
  },
  {
    id: 'groups',
    numeric: false,
    disablePadding: false,
    label: ConfigurationListStrings.GROUP_NAME
  },
  {
    id: 'failed_asset_count',
    numeric: false,
    disablePadding: false,
    label: ConfigurationListStrings.Failed
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: ConfigurationListStrings.ACTIVE_STATUS
  },
  {
    id: 'status_summary',
    numeric: false,
    disablePadding: false,
    label: ConfigurationListStrings.COMPLETION_STATUS
  }
]

type Order = 'asc' | 'desc'

interface Sort {
  order: Order
  orderBy: string
}

const ConfigurationList = (props: PropsFromRedux): JSX.Element => {
  const [configList, setConfigList] = React.useState<ConfigurationObj[]>([])
  const [showGroup, setshowGroup] = React.useState<GroupParaObj[]>([])
  const [page, setPage] = React.useState(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'asc', orderBy: 'templateName' })
  const rowsPerPage = 10
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const auth = useAuth()

  useEffect(() => {
    fetchConfigurationList()
  }, [page, sort])

  useEffect(() => {
    if (
      props.configurationsForStatusState.configurationsWithStatus !== null &&
      props.configurationsForStatusState.configurationsWithStatus.configuration_status
        .length > 0
    ) {
      setConfigList(
        props.configurationsForStatusState.configurationsWithStatus
          .configuration_status
      )
    }
  }, [props.configurationsForStatusState.configurationsWithStatus])

  const fetchConfigurationList = (): void => {
    page >= 1 &&
      props.fetchConfigurationsForStatus(
        page - 1,
        rowsPerPage,
        sort.order,
        sort.orderBy,
        auth.user?.profile.customer as string
      )
  }

  const handleRowClick = (templateId: string, templateName: string): void => {
    const templateDetails: TemplateDetailsState = {
      template_id: templateId,
      template_name: templateName
    }
    dispatch(changeTemplate(templateDetails))
    navigate('/configuration-details')
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    newOrderBy: keyof ConfigurationObj
  ): void => {
    const isAsc = sort.orderBy === newOrderBy && sort.order === 'asc'
    const toggledOrder = isAsc ? 'desc' : 'asc'
    setSort({ order: toggledOrder, orderBy: newOrderBy })
  }

  const getCompletionStatusBadge = (status: string): JSX.Element => {
    switch (status) {
      case 'Success':
        return <img data-testid="successBadge" src={successBadge} />
      case 'Pending':
        return <img data-testid="progressBadge" src={progressBadge} />
      case 'Failure':
        return <img data-testid="failedBadge" src={failedBadge} />
      default:
        return <img data-testid="notApplicableBadge" src={notApplicableBadge} />
    }
  }

  if (
    props.configurationsForStatusState.configurationsWithStatus !== null &&
    props.configurationsForStatusState.configurationsWithStatus
      .configuration_status.length > 0 &&
    props.configurationsForStatusState.httpStatus === 200
  ) {
    return (
      <>
        <Box sx={style} className="devices">
          <Paper
            sx={{
              width: '100%',
              mb: 2,
              backgroundColor: '#272727',
              boxShadow: 'unset',
              color: '#F0F0F0',
              marginBottom: '0'
            }}
          >
            <TableContainer className="device-table">
              <Table
                sx={{ minWidth: 750, color: '#f0f0f0' }}
                aria-labelledby="tableTitle"
                size={'small'}
              >
                <EnhancedTableHead
                  order={sort.order}
                  orderBy={sort.orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={configList.length}
                />
                <TableBody>
                  {configList.length > 0 &&
                    configList.map((row: ConfigurationObj, index: number) => {
                      const labelId = `enhanced-table-checkbox-${row.template_id}`

                      return (
                        <StyledTableRow
                          hover
                          tabIndex={-1}
                          key={row.template_id}
                          aria-label="devices-list-row"
                          data-testId={`row-${index}`}
                          sx={{ cursor: 'pointer' }}
                        >
                          <StyledTableCell
                            component="th"
                            id={labelId}
                            scope="row"
                            padding="none"
                            aria-label="device-name"
                            data-testid="device-name"
                            onClick={() => {
                              handleRowClick(row.template_id, row.template_name)
                            }}
                            sx={{
                              color: '#64C3FF !important',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '25vw'
                            }}
                          >
                            {row.template_name}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.asset_type_name.toUpperCase()}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.assets_associated}
                          </StyledTableCell>
                          <StyledTableCell>
                            {(row.groups === null ||
                              Object.keys(row.groups).length === 0)
                              ? ''
                              : row.groups.length === 1
                                ? <span data-testid={`assigned-single-group-${index}`} onClick={() => { setshowGroup(row.groups) }} style={{ color: '#64C3FF' }}>{row.groups[0]?.group_name}</span>
                                : (
                                  <span
                                    data-testid={`assigned-group-${index}`}
                                    onClick={() => {
                                      setshowGroup(row.groups)
                                    }}
                                    style={{ color: '#64C3FF' }}
                                  >
                                    {row.groups[0]?.group_name}+{row.groups.length - 1}
                                  </span>
                                  )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.failed_asset_count}
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{
                              color:
                                row.status.toUpperCase() === 'ACTIVE'
                                  ? '#68D560 !important'
                                  : '#FF644C !important'
                            }}
                          >
                            {(row.status[0].toUpperCase() +
                              row.status.slice(1).toLowerCase())}
                          </StyledTableCell>
                          <StyledTableCell>
                            {getCompletionStatusBadge(row.status_summary)}
                          </StyledTableCell>
                        </StyledTableRow>
                      )
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={
                props.configurationsForStatusState.configurationsWithStatus
                  .total_count
              }
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              siblingCount={1}
              boundaryCount={1}
            />
          </Paper>
        </Box>
        {showGroup.length > 0
          ? (
              getGroupModel(showGroup, () => {
                setshowGroup([])
              })
            )
          : (
          <></>
            )}
      </>
    )
  } else if (props.configurationsForStatusState.isLoading) {
    return (
      <div className="CircularprogressAmi">
        <CircularProgress />
      </div>
    )
  } else if (props.configurationsForStatusState.httpStatus === 401) {
    return <div className="authError"><AuthError errorMessage="Unauthorized" /></div>
  } else if (props.configurationsForStatusState.httpStatus === 403) {
    return <div className="authError"><AuthError errorMessage="accessForbidden" /></div>
  } else if (
    (props.configurationsForStatusState.configurationsWithStatus !== null &&
      props.configurationsForStatusState.configurationsWithStatus
        .configuration_status.length === 0 &&
      props.configurationsForStatusState.httpStatus === 200) ||
    props.configurationsForStatusState.error === 'No data Found for given tenantId'
  ) {
    return <div className="authError"><AuthError errorMessage="NoDataPresent" /></div>
  } else {
    return (
      <div className="authError"><AuthError errorMessage="cannotFetch" retry={fetchConfigurationList} /></div>
    )
  }
}

interface DispatchToProps {
  fetchConfigurationsForStatus: (
    page: number,
    size: number,
    sortdir: string,
    sortfield: string,
    tenantId: string
  ) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchConfigurationsForStatus: (
    page: number,
    size: number,
    sortdir: string,
    sortfield: string,
    tenantId: string
  ) =>
    dispatch(
      fetchConfigurationsForStatus(
        page,
        size,
        sortdir,
        sortfield,
        tenantId
      )
    )
})

interface StateToProps {
  configurationsForStatusState: ConfigurationsForStatusState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  configurationsForStatusState: state.configurationsForStatus
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(ConfigurationList)
